import { supabase } from '../supabaseClient';

/** 사이드바 프로필 말풍선 문구 — LS 캐시 + (건수·최신 updated_at) 버전 비교 */

const TABLE = 'platform_profile_bubble_messages';
const CATEGORY_TABLE = 'platform_profile_bubble_categories';
const STORAGE_KEY = 'cp.profileBubbleMessages.v2';
export const PROFILE_BUBBLE_MESSAGES_CHANGED_EVENT = 'cp:profile-bubble-messages-changed';

export type ProfileBubbleCategory = {
  id: string;
  name: string;
  sort_order: number;
  is_shown: boolean;
  created_at: string;
  updated_at: string;
};

export type ProfileBubbleMessage = {
  id: string;
  category_id: string;
  body: string;
  created_at: string;
  updated_at: string;
};

type CachePayload = { version: string; rows: ProfileBubbleMessage[] };

function mapCategory(row: Record<string, unknown>): ProfileBubbleCategory {
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    sort_order: Number(row.sort_order ?? 0),
    is_shown: row.is_shown !== false,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapRow(row: Record<string, unknown>): ProfileBubbleMessage {
  return {
    id: String(row.id ?? ''),
    category_id: String(row.category_id ?? ''),
    body: String(row.body ?? ''),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function buildVersion(count: number, latestUpdatedAt: string): string {
  return `${count}|${latestUpdatedAt}`;
}

function versionOfRows(rows: ProfileBubbleMessage[]): string {
  let latest = '';
  for (const r of rows) {
    if (Date.parse(r.updated_at) > Date.parse(latest || '1970-01-01')) latest = r.updated_at;
  }
  return buildVersion(rows.length, latest);
}

export function loadProfileBubbleMessagesCache(): CachePayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachePayload>;
    if (!parsed || typeof parsed.version !== 'string' || !Array.isArray(parsed.rows)) return null;
    return { version: parsed.version, rows: parsed.rows.map((r) => mapRow(r as Record<string, unknown>)) };
  } catch {
    return null;
  }
}

function saveCache(rows: ProfileBubbleMessage[]): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: CachePayload = { version: versionOfRows(rows), rows };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota */
  }
}

export function clearProfileBubbleMessagesCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

function invalidateAfterMutation(): void {
  clearProfileBubbleMessagesCache();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PROFILE_BUBBLE_MESSAGES_CHANGED_EVENT));
  }
}

async function fetchRemoteVersion(): Promise<string> {
  const { data, count, error } = await supabase
    .from(TABLE)
    .select('updated_at', { count: 'exact' })
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  const latest = data && data[0] ? String((data[0] as { updated_at?: unknown }).updated_at ?? '') : '';
  return buildVersion(count ?? 0, latest);
}

const CATEGORY_COLUMNS = 'id, name, sort_order, is_shown, created_at, updated_at';

export async function listProfileBubbleCategories(): Promise<ProfileBubbleCategory[]> {
  const { data, error } = await supabase
    .from(CATEGORY_TABLE)
    .select(CATEGORY_COLUMNS)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => mapCategory(r as Record<string, unknown>));
}

export async function createProfileBubbleCategory(name: string): Promise<ProfileBubbleCategory> {
  const trimmed = name.trim();
  const { data: last, error: lastError } = await supabase
    .from(CATEGORY_TABLE)
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1);
  if (lastError) throw lastError;
  const sortOrder = last && last[0] ? Number((last[0] as { sort_order?: unknown }).sort_order ?? 0) + 1 : 0;
  const { data, error } = await supabase
    .from(CATEGORY_TABLE)
    .insert({ name: trimmed, sort_order: sortOrder, is_shown: true })
    .select(CATEGORY_COLUMNS)
    .single();
  if (error) throw error;
  return mapCategory(data as Record<string, unknown>);
}

export async function setProfileBubbleCategoryShown(id: string, isShown: boolean): Promise<void> {
  const { error } = await supabase
    .from(CATEGORY_TABLE)
    .update({ is_shown: isShown, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PROFILE_BUBBLE_MESSAGES_CHANGED_EVENT));
  }
}

export async function deleteProfileBubbleCategory(id: string): Promise<void> {
  const { error } = await supabase.from(CATEGORY_TABLE).delete().eq('id', id);
  if (error) throw error;
  invalidateAfterMutation();
}

export async function listProfileBubbleMessages(options?: { force?: boolean }): Promise<ProfileBubbleMessage[]> {
  const cached = options?.force ? null : loadProfileBubbleMessagesCache();
  if (cached) {
    try {
      const remoteVersion = await fetchRemoteVersion();
      if (remoteVersion === cached.version) return cached.rows;
    } catch {
      return cached.rows;
    }
  }
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, category_id, body, created_at, updated_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  const rows = (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
  saveCache(rows);
  return rows;
}

export async function createProfileBubbleMessage(categoryId: string, body: string): Promise<ProfileBubbleMessage> {
  const { data: auth } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ category_id: categoryId, body: body.trim(), created_by: auth.user?.id ?? null })
    .select('id, category_id, body, created_at, updated_at')
    .single();
  if (error) throw error;
  invalidateAfterMutation();
  return mapRow(data as Record<string, unknown>);
}

export async function updateProfileBubbleMessage(id: string, body: string): Promise<ProfileBubbleMessage> {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ body: body.trim(), updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('id, category_id, body, created_at, updated_at')
    .single();
  if (error) throw error;
  invalidateAfterMutation();
  return mapRow(data as Record<string, unknown>);
}

export async function deleteProfileBubbleMessage(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) throw error;
  invalidateAfterMutation();
}
